/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Angle,
  ArrowOption,
  ElementType,
  getThickness,
  getThicknessAnchor,
  Rect,
  Text,
  ThicknessAnchor,
  Trapezoid,
  Triangle,
} from '@ritabygglov/core';
import { computeTriangleVertices } from '../../services/Actions/Triangle';
import { computeTrapezoidVertices } from '../../services/Actions/Trapezoid';
import {
  getInnerWallStrokeWidth,
  getWallLineWeight,
  isConnected,
  offsetPolygonSafe,
} from '../../helpers/utility';
import { getPaddedHitStroke } from '../../helpers/hitAreaScale';
import { Coordinate, Side } from '../../types';

// https://math.stackexchange.com/questions/175896/finding-a-point-along-a-line-a-certain-distance-away-from-another-point
export const pointCloseToCorner = (
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  distanceFromCorner: number
) => {
  const d = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);
  const t = distanceFromCorner / d;
  return {
    x: (1 - t) * x0 + t * x1,
    y: (1 - t) * y0 + t * y1,
  };
};

export function createRoundedLine(
  coordinates: [number, number][],
  radius: number = 20
): string {
  const pathSegments = [];

  for (let i = 0; i < coordinates.length; i++) {
    const curPoint = coordinates[i];

    if (i === 0) {
      // first point
      pathSegments.push(`M ${curPoint[0]} ${curPoint[1]}`);
    } else if (i === coordinates.length - 1) {
      // last point
      pathSegments.push(`L ${curPoint[0]} ${curPoint[1]}`);
    } else {
      const prevPoint = coordinates[i - 1];
      const nextPoint = coordinates[i + 1];
      const firstPoint = pointCloseToCorner(...curPoint, ...prevPoint, radius);
      const secondPoint = pointCloseToCorner(...curPoint, ...nextPoint, radius);
      // Quadratic bezier curve: https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths
      pathSegments.push(
        `L ${firstPoint.x} ${firstPoint.y} Q ${curPoint[0]} ${curPoint[1]}, ${secondPoint.x} ${secondPoint.y}`
      );
    }
  }

  return pathSegments.join(' ');
}

export function createRoundedLinePerPoint(
  coordinates: [number, number, boolean, number][]
): string {
  const pathSegments: string[] = [];
  const closed = isConnected(
    coordinates as unknown as [number, number, unknown][]
  );

  // For closed paths, point 0 is also a corner (predecessor = second-to-last point).
  // Pre-compute its approach so both the M command and the final closing segment use it.
  let closedStartApproach: { x: number; y: number } | null = null;

  if (closed && coordinates.length >= 3) {
    const [ax, ay, , radiusA] = coordinates[0];
    const [px, py] = coordinates[coordinates.length - 2];
    const [nx, ny] = coordinates[1];
    if (radiusA > 0) {
      const segInLen = Math.sqrt((ax - px) ** 2 + (ay - py) ** 2);
      const segOutLen = Math.sqrt((nx - ax) ** 2 + (ny - ay) ** 2);
      const clampedRadius = Math.min(radiusA, segInLen / 2, segOutLen / 2);
      closedStartApproach = pointCloseToCorner(ax, ay, px, py, clampedRadius);
      const secondPoint = pointCloseToCorner(ax, ay, nx, ny, clampedRadius);
      pathSegments.push(`M ${closedStartApproach.x} ${closedStartApproach.y}`);
      pathSegments.push(`Q ${ax} ${ay}, ${secondPoint.x} ${secondPoint.y}`);
    } else {
      pathSegments.push(`M ${ax} ${ay}`);
    }
  } else {
    const [cx, cy] = coordinates[0];
    pathSegments.push(`M ${cx} ${cy}`);
  }

  for (let i = 1; i < coordinates.length; i++) {
    const [cx, cy, , radius] = coordinates[i];

    if (i === coordinates.length - 1) {
      // Last/connected point: route to the approach so Z closes exactly at M.
      if (closedStartApproach) {
        pathSegments.push(
          `L ${closedStartApproach.x} ${closedStartApproach.y}`
        );
      } else {
        pathSegments.push(`L ${cx} ${cy}`);
      }
    } else if (radius > 0) {
      const [px, py] = coordinates[i - 1];
      const [nx, ny] = coordinates[i + 1];
      const segInLen = Math.sqrt((cx - px) ** 2 + (cy - py) ** 2);
      const segOutLen = Math.sqrt((nx - cx) ** 2 + (ny - cy) ** 2);
      const clampedRadius = Math.min(radius, segInLen / 2, segOutLen / 2);
      const firstPoint = pointCloseToCorner(cx, cy, px, py, clampedRadius);
      const secondPoint = pointCloseToCorner(cx, cy, nx, ny, clampedRadius);
      pathSegments.push(
        `L ${firstPoint.x} ${firstPoint.y} Q ${cx} ${cy}, ${secondPoint.x} ${secondPoint.y}`
      );
    } else {
      pathSegments.push(`L ${cx} ${cy}`);
    }
  }

  if (closed) {
    pathSegments.push('Z');
  }

  return pathSegments.join(' ');
}

export function createLine(coordinates: [number, number, boolean][]): string {
  const pathSegments = [];

  for (let i = 0; i < coordinates.length; i++) {
    const curPoint = coordinates[i];

    if (i === 0) {
      // first point
      pathSegments.push(`M ${curPoint[0]} ${curPoint[1]}`);
    } else {
      pathSegments.push(`L ${curPoint[0]} ${curPoint[1]}`);
    }
  }

  if (isConnected(coordinates)) {
    pathSegments.push('Z');
  }

  return pathSegments.join(' ');
}

export function createRoundedRect(rect: Rect, radius = 10) {
  const { x, y, width, height } = rect;
  const pathSegments = [];
  pathSegments.push(`M ${x + radius} ${y} L ${x + width - radius} ${y}`);
  // top right corner
  pathSegments.push(`Q ${x + width} ${y}, ${x + width} ${y + radius}`);

  pathSegments.push(`L ${x + width} ${y + height - radius}`);

  // bottom right corner
  pathSegments.push(
    `Q ${x + width} ${y + height}, ${x + width - radius} ${y + height}`
  );
  pathSegments.push(`L ${x + radius} ${y + height}`);

  // bottom left corner
  pathSegments.push(`Q ${x} ${y + height}, ${x} ${y + height - radius}`);

  pathSegments.push(`L ${x} ${y + radius}`);

  // top left corner
  pathSegments.push(`Q ${x} ${y}, ${x + radius} ${y}`);

  return pathSegments.join(' ');
}

/**
 * Path of the rectangle's geometry line, i.e. the centreline of the rendered
 * stroke. Where that line sits relative to the stored bounding box is decided
 * by the anchor: Outer (the historic behaviour) insets it by half the
 * thickness so the band grows inward, Centre straddles the box, Inner offsets
 * outward.
 */
export function createRect({
  x,
  y,
  width,
  height,
  thickness,
  anchor = ThicknessAnchor.Outer,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  thickness: number;
  anchor?: ThicknessAnchor;
}) {
  const { x: insetX, y: insetY } = getBandInset(
    thickness,
    anchor,
    width,
    height
  );

  const left = x + insetX;
  const right = x + width - insetX;
  const top = y + insetY;
  const bottom = y + height - insetY;

  const pathSegments = [];
  pathSegments.push(`M ${left} ${top} L ${right} ${top}`);
  pathSegments.push(`L ${right} ${bottom}`);
  pathSegments.push(`L ${left} ${bottom}`);
  pathSegments.push(`L ${left} ${top}`);
  pathSegments.push('Z');

  return pathSegments.join(' ');
}

/**
 * Inset of the geometry line from the bounding box for the given anchor,
 * clamped so a band wider than the shape collapses onto the centre rather than
 * inverting the path.
 */
function getBandInset(
  thickness: number,
  anchor: ThicknessAnchor,
  width: number,
  height: number
) {
  if (anchor === ThicknessAnchor.Centre) return { x: 0, y: 0 };

  const offset =
    anchor === ThicknessAnchor.Inner ? -thickness / 2 : thickness / 2;

  return {
    x: Math.min(offset, Math.abs(width) / 2),
    y: Math.min(offset, Math.abs(height) / 2),
  };
}

/**
 * Signed offset applied to a polygon's vertices for the given anchor. Outer
 * means the stored geometry is the outer face, so the geometry line moves
 * inward (negative) by half the thickness; Inner moves outward; Centre leaves
 * the vertices alone, which is the historic behaviour of every polygon shape.
 */
const getPolygonBandOffset = (
  thickness: number,
  anchor: ThicknessAnchor
): number => {
  if (anchor === ThicknessAnchor.Outer) return -thickness / 2;
  if (anchor === ThicknessAnchor.Inner) return thickness / 2;
  return 0;
};

/**
 * Offsets a shape's vertices onto its geometry line, collapsing back to the
 * original outline when the band is wider than the shape — an inverted polygon
 * would render as a self-intersecting mess.
 */
const offsetShapeVertices = (
  vertices: Coordinate[],
  thickness: number,
  anchor: ThicknessAnchor
): Coordinate[] => {
  const offset = getPolygonBandOffset(thickness, anchor);
  if (offset === 0) return vertices;

  return offsetPolygonSafe(vertices, offset) ?? vertices;
};

const toPolygonPath = (vertices: Coordinate[]): string =>
  vertices.map(({ x, y }, i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ') +
  ' Z';

const toOpenPolylinePath = (vertices: Coordinate[]): string =>
  vertices.map(({ x, y }, i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');

export const SHARP_CORNER_MITER_LIMIT = 20;

export const SHARP_CORNER_STYLE: React.CSSProperties = {
  strokeLinejoin: 'miter',
  strokeMiterlimit: SHARP_CORNER_MITER_LIMIT,
};

export function createTrianglePath(triangle: Triangle): string {
  const { apex, baseRight, baseLeft } = computeTriangleVertices(
    triangle.pointRatio,
    triangle.width,
    triangle.height
  );
  const { x, y } = triangle;
  const vertices = [apex, baseRight, baseLeft].map((v) => ({
    x: x + v.x,
    y: y + v.y,
  }));

  return toPolygonPath(
    offsetShapeVertices(
      vertices,
      getThickness(triangle),
      getThicknessAnchor(triangle)
    )
  );
}

/**
 * An angle is a triangle without its base. The thickness band is still computed
 * from the closed triangle - the offset outline is what decides where the two
 * legs land - and only the base edge is dropped when the path is emitted.
 */
export function getAngleLegVertices(
  angle: Angle
): [Coordinate, Coordinate, Coordinate] {
  const { apex, baseRight, baseLeft } = computeTriangleVertices(
    angle.pointRatio,
    angle.width,
    angle.height
  );
  const { x, y } = angle;
  const vertices = [apex, baseRight, baseLeft].map((v) => ({
    x: x + v.x,
    y: y + v.y,
  }));

  const [offsetApex, offsetBaseRight, offsetBaseLeft] = offsetShapeVertices(
    vertices,
    getThickness(angle),
    getThicknessAnchor(angle)
  );

  return [offsetBaseLeft, offsetApex, offsetBaseRight];
}

export function createAnglePath(angle: Angle): string {
  return toOpenPolylinePath(getAngleLegVertices(angle));
}

const trimTowards = (
  end: Coordinate,
  target: Coordinate,
  distance: number
): Coordinate => {
  const length = Math.hypot(target.x - end.x, target.y - end.y);
  if (length === 0) return end;
  return pointCloseToCorner(
    end.x,
    end.y,
    target.x,
    target.y,
    Math.min(distance, length / 2)
  );
};

/**
 * The white inner line of an outlined angle. Both open ends are pulled back
 * towards the apex by one wall line weight so the stroke underneath shows as
 * a line closing off each end of the band.
 */
export function createAngleInnerWallPath(angle: Angle): string {
  const [baseLeft, apex, baseRight] = getAngleLegVertices(angle);
  const wallLineWeight = getWallLineWeight(getThickness(angle));

  return toOpenPolylinePath([
    trimTowards(baseLeft, apex, wallLineWeight),
    apex,
    trimTowards(baseRight, apex, wallLineWeight),
  ]);
}

export function createTrapezoidPath(trapezoid: Trapezoid): string {
  const computed = computeTrapezoidVertices(
    trapezoid.topWidth,
    trapezoid.bottomWidth,
    trapezoid.height
  );
  if (!computed) return '';
  const { x, y } = trapezoid;
  const vertices = computed.vertices.map(([vx, vy]) => ({
    x: x + vx,
    y: y + vy,
  }));

  return toPolygonPath(
    offsetShapeVertices(
      vertices,
      getThickness(trapezoid),
      getThicknessAnchor(trapezoid)
    )
  );
}

const HOLLOW_FILL_VALUES = new Set(['none', 'transparent', '']);

export const HIT_AREA_SCALE_FACTOR = 2;
export const HIT_AREA_MIN = 1;

export const getHitAreaStrokeWidth = (
  visiblethickness: number,
  scale: number
): number =>
  getPaddedHitStroke(
    visiblethickness,
    HIT_AREA_MIN,
    HIT_AREA_SCALE_FACTOR,
    scale
  );

export const hasVisibleFill = (
  style?: React.CSSProperties,
  patternValue?: string
): boolean => {
  if (patternValue) return true;
  const fill = style?.fill;
  if (fill === undefined || fill === null) return false;
  return !HOLLOW_FILL_VALUES.has(String(fill).trim().toLowerCase());
};

/**
 * Invisible hit area element traces the outline of the shape
 * but with a wide transparent stroke. Shares the element id so DOM-based hit-testing
 * keeps working.
 */
export const renderHitArea = (
  element: Rect | Text | Triangle | Trapezoid | Angle,
  scale: number
) => {
  const { type, id } = element;
  if (type === ElementType.Text) return null;

  const hitAreaKey = `hit-${id}`;
  const common = {
    id,
    fill: 'none',
    stroke: 'transparent',
    strokeWidth: getHitAreaStrokeWidth(getThickness(element), scale),
    className: 'hit-area',
    'data-testid': `${type}-hit-area`,
  };

  if (type === ElementType.Rect) {
    const { x, y, width, height } = element;
    return (
      <path
        key={hitAreaKey}
        {...common}
        d={createRect({
          x,
          y,
          width,
          height,
          thickness: getThickness(element),
          anchor: getThicknessAnchor(element),
        })}
      />
    );
  } else if (type === ElementType.Triangle) {
    return (
      <path
        key={hitAreaKey}
        {...common}
        strokeLinejoin="miter"
        strokeMiterlimit={SHARP_CORNER_MITER_LIMIT}
        d={createTrianglePath(element as Triangle)}
      />
    );
  } else if (type === ElementType.Trapezoid) {
    return (
      <path
        key={hitAreaKey}
        {...common}
        strokeLinejoin="miter"
        strokeMiterlimit={SHARP_CORNER_MITER_LIMIT}
        d={createTrapezoidPath(element as Trapezoid)}
      />
    );
  } else if (type === ElementType.Angle) {
    return (
      <path
        key={hitAreaKey}
        {...common}
        strokeLinejoin="miter"
        strokeMiterlimit={SHARP_CORNER_MITER_LIMIT}
        d={createAnglePath(element as Angle)}
      />
    );
  }

  return null;
};

export const renderSvgElement = (
  element: Rect | Text | Triangle | Trapezoid | Angle,
  classes: string,
  style: React.CSSProperties
) => {
  const { type, id } = element;
  const thickness = getThickness(element);
  const strokedStyle = { ...style, strokeWidth: thickness };
  if (type === ElementType.Rect) {
    const { x, y, width, height } = element;
    return (
      <path
        key={id}
        style={strokedStyle}
        id={id}
        d={createRect({
          x,
          y,
          width,
          height,
          thickness,
          anchor: getThicknessAnchor(element),
        })}
        className={classes}
        data-testid="rect-svg"
      />
    );
  } else if (type === ElementType.Triangle) {
    return (
      <path
        key={id}
        style={{ ...strokedStyle, ...SHARP_CORNER_STYLE }}
        id={id}
        d={createTrianglePath(element as Triangle)}
        className={classes}
        data-testid="triangle-svg"
      />
    );
  } else if (type === ElementType.Trapezoid) {
    return (
      <path
        key={id}
        style={{ ...strokedStyle, ...SHARP_CORNER_STYLE }}
        id={id}
        d={createTrapezoidPath(element as Trapezoid)}
        className={classes}
        data-testid="trapezoid-svg"
      />
    );
  } else if (type === ElementType.Angle) {
    return (
      <path
        key={id}
        style={{ ...strokedStyle, ...SHARP_CORNER_STYLE, fill: 'none' }}
        id={id}
        d={createAnglePath(element as Angle)}
        className={classes}
        data-testid="angle-svg"
      />
    );
  } else if (type === ElementType.Text) {
    const {
      type: _type,
      renderingOrder,
      userVersion,
      diagonalHatch: _diagonalHatch,
      isOutlined: _isOutlined,
      showDimensions: _showDimensions,
      thickness: _thickness,
      thicknessAnchor: _thicknessAnchor,
      ...props
    } = element;
    return (
      <rect
        key={element.id}
        {...props}
        className={classes}
        data-testid="text"
      />
    );
  }
  return null;
};

/**
 * White line drawn over an outlined shape's stroke, leaving only its two faces
 * visible so the shape reads as a wall.
 */
export const renderOutlinedInnerWall = (
  element: Rect | Text | Triangle | Trapezoid | Angle,
  classes: string
) => {
  const { type, id } = element;
  if (type === ElementType.Text || !element.isOutlined) return null;

  const thickness = getThickness(element);
  const common = {
    id,
    stroke: 'white',
    fill: 'none',
    strokeWidth: getInnerWallStrokeWidth(thickness),
    className: classes,
    'data-testid': `${type}-outlined-inner`,
  };
  const style: React.CSSProperties = { pointerEvents: 'none' };

  if (type === ElementType.Rect) {
    const { x, y, width, height } = element;
    return (
      <path
        {...common}
        style={style}
        d={createRect({
          x,
          y,
          width,
          height,
          thickness,
          anchor: getThicknessAnchor(element),
        })}
      />
    );
  } else if (type === ElementType.Triangle) {
    return (
      <path
        {...common}
        style={{ ...style, ...SHARP_CORNER_STYLE }}
        d={createTrianglePath(element as Triangle)}
      />
    );
  } else if (type === ElementType.Trapezoid) {
    return (
      <path
        {...common}
        style={{ ...style, ...SHARP_CORNER_STYLE }}
        d={createTrapezoidPath(element as Trapezoid)}
      />
    );
  }

  return (
    <path
      {...common}
      style={{ ...style, ...SHARP_CORNER_STYLE }}
      d={createAngleInnerWallPath(element as Angle)}
    />
  );
};

export const getArrowStyle = (
  side: Side,
  option: ArrowOption,
  arrowStyle: React.SVGAttributes<SVGPathElement>['stroke']
) => {
  if (option === ArrowOption.slim) {
    const d = side === Side.left ? 'M1,1 L3,3 L1,5' : 'M3,1 L1,3 L3,5';
    return (
      <path
        d={d}
        fill="transparent"
        stroke={arrowStyle}
        strokeWidth={1.5}
        strokeLinejoin="round"
        className="dimension-arrow-marker"
      />
    );
  } else if (option === ArrowOption.thick) {
    const d = side === Side.left ? 'M0,0 V4 L2,2 Z' : 'M2,0 V4 L0,2 Z';
    return <path d={d} fill={arrowStyle} className="dimension-arrow-marker" />;
  } else {
    return <path d="" />;
  }
};
